import type { GuideDataStatus } from '@/onboarding/dataContext'
import type { GuideExperience, GuideTask } from '@/onboarding/types'
import apiUser from '@/api/modules/user'
import { getEdition } from '@/onboarding/config'
import { resetEnterpriseDataContext } from '@/onboarding/dataContext'

interface OnboardingContext {
  versionId: string
  account: string
  tenantId: string
}

type RemoteStepStatus = 'in_progress' | 'completed' | 'skipped' | 'deferred'
// 后端不再限制枚举，步骤状态按前端当前值原样保存。
type PatchStepStatus = RemoteStepStatus
type RemoteCursorStatus = 'idle' | 'active' | 'paused' | 'partial' | 'deferred' | 'completed'

interface RemoteStepState {
  taskId: string
  stepId: string
  status: RemoteStepStatus
  source?: string | null
  completedAt?: string | null
  updatedAt?: string | null
}

interface RemoteSetupState {
  complete?: boolean
  step?: number
  experience?: GuideExperience | null
  goals?: string[]
  mode?: 'full' | 'quick'
  laterUntil?: string | null
  reminderDisabled?: boolean
}

// PATCH 接口对 setup.experience 的校验比 GET 更严格：
// GET 无进度/未选择经验时可能返回 null；PATCH 只允许 new/similar/expert，不能提交 null 或 quick。
type PatchSetupExperience = Exclude<GuideExperience, 'quick'>

interface PatchSetupState {
  complete: boolean
  step: number
  experience: PatchSetupExperience
  goals: string[]
  mode: 'full' | 'quick'
  laterUntil: string | null
  reminderDisabled: boolean
}

interface RemoteCursorState {
  // 兼容历史 GET 字段及 PATCH 原样保存后返回的字段。
  taskId?: string | null
  stepId?: string | null
  currentTaskId?: string | null
  currentStepId?: string | null
  status?: RemoteCursorStatus
  resumeOnLoad?: boolean
  lastRouteKey?: string | null
  pauseReason?: string | null
}

interface RemoteOnboardingState {
  exists: boolean
  guideKey?: string
  contentVersion?: string
  schemaVersion?: number
  revision?: number
  resetEpoch?: number
  setup?: RemoteSetupState | null
  cursor?: RemoteCursorState | null
  stepStates?: RemoteStepState[]
  stepChanges?: PatchStepChange[]
  completedTaskIds?: string[]
}

interface PatchStepChange {
  taskId: string
  stepId: string
  status: PatchStepStatus
  source: 'tutorial_confirm'
  clientOccurredAt: string
}

interface PatchCursorState {
  // PATCH 接口字段与 GET 不一致：这里必须使用 currentTaskId / currentStepId。
  currentTaskId: string
  currentStepId: string
  status: 'idle' | 'active' | 'paused'
  resumeOnLoad: boolean
  lastRouteKey: string | null
  pauseReason: string | null
}

interface RemotePatchSnapshot {
  stepChanges: PatchStepChange[]
  cursor: PatchCursorState | null
  setup: PatchSetupState | null
}

interface PersistedOnboardingState {
  experience?: GuideExperience
  goals?: string[]
  activeTaskId?: string
  activeStepIndex?: number
  completedTaskIds?: string[]
  completedStepIds?: string[]
  skippedStepIds?: string[]
  deferredStepIds?: string[]
  initialized?: boolean
  laterUntil?: number
  status?: 'idle' | 'running' | 'paused'
  launcherPosition?: {
    edge: 'left' | 'right'
    yRatio: number | null
  }
  launcherDismissed?: boolean
  // 点击“重新设置”后，在第四步最终确认时调用独立 reset 接口。
  resetProgressAfterSetupConfirm?: boolean
}

const useOnboardingStore = defineStore('onboarding', () => {
  const welcomeVisible = ref(false)
  const taskCenterVisible = ref(false)
  const status = ref<'idle' | 'running' | 'paused'>('idle')
  const versionId = ref('')
  const account = ref('')
  const tenantId = ref('')
  const experience = ref<GuideExperience>('new')
  const goals = ref<string[]>(['project'])
  const setupStep = ref(0)
  const setupExperience = ref<GuideExperience | ''>('')
  const setupGoals = ref<string[]>([])
  const showAllTasks = ref(false)
  const activeTaskId = ref('')
  const activeStepIndex = ref(0)
  const completedTaskIds = ref<string[]>([])
  const completedStepIds = ref<string[]>([])
  const skippedStepIds = ref<string[]>([])
  const deferredStepIds = ref<string[]>([])
  // 保存最近一次成功提交的累计快照，用于后续 PATCH 保留历史步骤的 clientOccurredAt。
  const remoteStepChanges = ref<PatchStepChange[]>([])
  const stepDataStatuses = ref<Record<string, GuideDataStatus>>({})
  // 本次登录中，后端 routeList 未返回对应页面的步骤。仅运行时过滤，不持久化。
  const runtimeUnavailableStepIds = ref<string[]>([])
  const accessibleTaskIds = ref<string[] | null>(null)
  const workspaceRoute = ref('')
  const initialized = ref(false)
  const laterUntil = ref(0)
  const targetMissing = ref(false)
  const contextSignature = ref('')
  const launcherPosition = ref<{ edge: 'left' | 'right', yRatio: number | null }>({
    edge: 'right',
    yRatio: null,
  })
  const launcherDismissed = ref(false)
  // 用户从任务中心点击“重新设置”后，不立即清空旧任务进度。
  // 只有欢迎页第四步最终确认时，才调用 reset 清空服务端任务和步骤。
  const resetProgressAfterSetupConfirm = ref(false)
  let setupCompletionInFlight = false
  // 同一次页面生命周期内额外保留“重新设置”来源，避免异步恢复本地状态时覆盖 ref 标记。
  let restartSetupSessionActive = false

  function isRestartSetupFlow() {
    return restartSetupSessionActive || resetProgressAfterSetupConfirm.value
  }

  // ===== 服务端新手引导进度状态 =====
  // remoteExists 是“是否自动弹欢迎页”的唯一业务判断依据：
  // false -> 自动弹；true -> 不自动弹；null -> 尚未查询或查询失败，不自动弹。
  const remoteExists = ref<boolean | null>(null)
  const remoteStateLoaded = ref(false)
  const remoteStateLoadFailed = ref(false)
  const remoteSyncing = ref(false)
  const remoteSyncFailed = ref(false)
  const remoteRevision = ref(0)
  const remoteResetEpoch = ref(0)
  // 防止同一次页面生命周期内，因为路由 watcher 多次触发而重复自动打开欢迎页。
  // 刷新页面或重新登录后该值会重置；若后端仍 exists=false，仍会再次自动弹。
  const autoWelcomeOpened = ref(false)
  let remoteLoadPromise: Promise<boolean> | null = null
  let remotePatchChain: Promise<void> = Promise.resolve()

  const edition = computed(() => getEdition(versionId.value))
  const tasks = computed(() => accessibleTaskIds.value
    ? edition.value.tasks.filter(task => accessibleTaskIds.value?.includes(task.id))
    : edition.value.tasks)
  const storageKey = computed(() => [
    'surveysaas.onboarding',
    window.location.hostname,
    tenantId.value || 'tenant',
    account.value || 'user',
    edition.value.id,
    edition.value.guideVersion,
  ].join(':'))
  const currentTask = computed(() => tasks.value.find(task => task.id === activeTaskId.value) || null)
  const allTasksCompleted = computed(() => (
    tasks.value.length > 0
    && tasks.value.every(task => completedTaskIds.value.includes(task.id))
  ))

  function stepsForExperience(task: GuideTask | null, selectedExperience: GuideExperience) {
    if (!task) {
      return []
    }
    const profile = selectedExperience === 'quick' ? 'similar' : selectedExperience
    const stepIds = edition.value.experienceStepIds[profile]?.[task.id] || []
    if (!stepIds.length) {
      return [...task.steps]
    }
    const stepMap = new Map(task.steps.map(step => [step.id, step]))
    return stepIds.map(stepId => stepMap.get(stepId)).filter((step): step is GuideTask['steps'][number] => Boolean(step))
  }

  function recommendedTaskIds(selectedGoals: string[], selectedExperience: GuideExperience) {
    if (selectedExperience === 'quick') {
      return ['interface-tour']
    }
    const selectedTaskIds = new Set<string>()
    const effectiveGoals = selectedGoals.length ? selectedGoals : ['project']
    effectiveGoals.forEach((goal) => {
      edition.value.goalTaskMap[goal]?.forEach(taskId => selectedTaskIds.add(taskId))
    })
    if (selectedExperience === 'expert') {
      selectedTaskIds.delete('interface-tour')
    }
    return edition.value.taskOrder.filter(taskId => (
      selectedTaskIds.has(taskId) && tasks.value.some(task => task.id === taskId)
    ))
  }

  const currentSteps = computed(() => stepsForExperience(currentTask.value, experience.value)
    .filter(step => !runtimeUnavailableStepIds.value.includes(step.id)))
  const currentStep = computed(() => currentSteps.value[activeStepIndex.value] || null)
  const recommendedIds = computed(() => recommendedTaskIds(goals.value, experience.value))
  const setupRecommendedIds = computed(() => recommendedTaskIds(
    setupGoals.value,
    setupExperience.value || 'new',
  ))
  const setupRecommendedTasks = computed(() => setupRecommendedIds.value
    .map(taskId => tasks.value.find(task => task.id === taskId))
    .filter((task): task is GuideTask => Boolean(task))
    .map(task => ({
      ...task,
      steps: stepsForExperience(task, setupExperience.value || 'new'),
    })))
  const visibleTasks = computed(() => (showAllTasks.value
    ? tasks.value
    : recommendedIds.value
        .map(taskId => tasks.value.find(task => task.id === taskId))
        .filter((task): task is GuideTask => Boolean(task)))
    .map(task => ({
      ...task,
      steps: stepsForExperience(task, experience.value),
    })))
  const completedVisibleTaskCount = computed(() => visibleTasks.value
    .filter(task => completedTaskIds.value.includes(task.id))
    .length)
  const progress = computed(() => visibleTasks.value.length
    ? Math.round((completedVisibleTaskCount.value / visibleTasks.value.length) * 100)
    : 0)
  function taskEstimatedMinutes(task: GuideTask) {
    return Math.max(1, Math.ceil(stepsForExperience(task, experience.value).length / 3))
  }

  const estimatedMinutes = computed(() => visibleTasks.value.reduce(
    (total, task) => total + taskEstimatedMinutes(task),
    0,
  ))

  // 三个版本统一使用同一套后端进度持久化逻辑。
  // 是否自动弹出欢迎页统一只认 GET 返回的 exists，不再按版本做任何区分。

  function createMutationId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
    return `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`
  }

  function normalizeRemoteCursorStatus(value: RemoteCursorStatus | undefined): 'idle' | 'running' | 'paused' {
    if (value === 'paused') {
      return 'paused'
    }
    // GET 即使返回 active，也不能在系统初始化时自动启动 Tour。
    // 有服务端数据时只恢复位置，真正进入某个任务必须由用户在任务中心主动点击。
    if (value === 'active') {
      return 'paused'
    }
    return 'idle'
  }

  function normalizeRemoteSetupStep(value: unknown) {
    // 欢迎页前端内部使用 0~3 作为数组下标；接口 setup.step 使用 1~4 表示“第几步”。
    // 因此 GET 恢复时统一做 1~4 -> 0~3 的转换，避免第四步在前端出现越界值 4。
    const remoteStep = Number(value)
    if (!Number.isFinite(remoteStep)) {
      return 0
    }
    return Math.max(0, Math.min(3, Math.trunc(remoteStep) - 1))
  }

  function resetProgressToRemoteEmptyState() {
    // GET exists=false 代表服务端没有任何教程进度。
    // 本地旧缓存不能把该账号误判成“已经操作过教程”，因此业务进度恢复默认值。
    experience.value = 'new'
    goals.value = ['project']
    setupStep.value = 0
    setupExperience.value = ''
    setupGoals.value = []
    showAllTasks.value = false
    activeTaskId.value = ''
    activeStepIndex.value = 0
    completedTaskIds.value = []
    completedStepIds.value = []
    skippedStepIds.value = []
    deferredStepIds.value = []
    remoteStepChanges.value = []
    initialized.value = false
    laterUntil.value = 0
    status.value = 'idle'
    targetMissing.value = false
    resetProgressAfterSetupConfirm.value = false
  }

  function restoreRemoteState(data: RemoteOnboardingState) {
    remoteExists.value = Boolean(data.exists)
    remoteRevision.value = Number(data.revision) || 0
    remoteResetEpoch.value = Number(data.resetEpoch) || 0

    if (!data.exists) {
      resetProgressToRemoteEmptyState()
      return
    }

    const remoteSetup = data.setup || null
    const remoteExperience = remoteSetup?.experience
    experience.value = remoteExperience === 'new'
      || remoteExperience === 'similar'
      || remoteExperience === 'expert'
      || remoteExperience === 'quick'
      ? remoteExperience
      : 'new'
    goals.value = Array.isArray(remoteSetup?.goals) && remoteSetup!.goals!.length
      ? [...remoteSetup!.goals!]
      : ['project']
    initialized.value = Boolean(remoteSetup?.complete)
    setupStep.value = normalizeRemoteSetupStep(remoteSetup?.step)
    setupExperience.value = remoteExperience || ''
    // goals 保持当前前端配置中的原始值，不再做固定枚举映射。
    // 后端已取消旧的 goals 固定白名单，因此 GET/PATCH 都直接使用当前代码里的 goal.id。
    setupGoals.value = Array.isArray(remoteSetup?.goals) ? [...remoteSetup!.goals!] : []
    laterUntil.value = 0

    applyRemoteStepSummary(data, true)

    // 兼容历史 GET 的 taskId/stepId，以及 PATCH 原样保存后返回的
    // currentTaskId/currentStepId。
    const remoteTaskId = String(data.cursor?.currentTaskId || data.cursor?.taskId || '')
    const remoteStepId = String(data.cursor?.currentStepId || data.cursor?.stepId || '')
    const localCursorStepId = getLocalStepId(remoteTaskId, remoteStepId)
    const cursorTask = tasks.value.find(item => item.id === remoteTaskId) || null
    const cursorTaskSteps = stepsForExperience(cursorTask, experience.value)
    const cursorTaskAlreadyCompleted = Boolean(remoteTaskId) && (
      completedTaskIds.value.includes(remoteTaskId)
      || (
        cursorTaskSteps.length > 0
        && cursorTaskSteps.every(step => completedStepIds.value.includes(step.id))
      )
    )

    // 服务端若因为历史数据仍返回“已完成任务最后一步”的旧 cursor，前端必须忽略。
    // 已完成任务重新登录后只能在任务中心展示“已学完”，不能再次自动恢复最后一步 Tour。
    if (!remoteTaskId || !localCursorStepId || cursorTaskAlreadyCompleted) {
      activeTaskId.value = ''
      activeStepIndex.value = 0
      status.value = 'idle'
    }
    else {
      activeTaskId.value = remoteTaskId
      const matchedIndex = cursorTaskSteps.findIndex(step => step.id === localCursorStepId)
      activeStepIndex.value = matchedIndex >= 0 ? matchedIndex : 0
      status.value = normalizeRemoteCursorStatus(data.cursor?.status)
    }
    sanitizeState()
  }

  /**
   * 登录后查询后端权威进度。
   * 自动弹欢迎页只认 GET 返回的 exists：
   * - exists=false：允许自动弹；
   * - exists=true：只恢复进度，不自动弹；
   * - GET 失败：不自动弹，避免把网络异常误判为新用户。
   */
  async function loadRemoteProgress() {
    if (remoteStateLoaded.value) {
      return !remoteStateLoadFailed.value
    }
    if (remoteLoadPromise) {
      return remoteLoadPromise
    }

    remoteLoadPromise = (async () => {
      remoteStateLoadFailed.value = false
      try {
        const response = await apiUser.getOnboardingProgress()
        const data = response?.data as RemoteOnboardingState | undefined
        if (!data || typeof data.exists !== 'boolean') {
          throw new Error('新手引导进度接口返回格式不正确')
        }
        restoreRemoteState(data)
        remoteStateLoaded.value = true
        return true
      }
      catch (error) {
        console.warn('[Onboarding] 查询服务端新手引导进度失败，本次不自动弹出欢迎页。', error)
        remoteExists.value = null
        remoteStateLoadFailed.value = true
        remoteStateLoaded.value = true
        return false
      }
      finally {
        remoteLoadPromise = null
      }
    })()

    return remoteLoadPromise
  }

  /**
   * 把前端教程 step.id 转换成后端保存使用的稳定步骤 ID。
   *
   * 后端约定格式：taskId-步骤序号（从 01 开始）。
   * 示例：
   * - interface-tour 的第 1 步 top-nav -> interface-tour-01
   * - customer-management 的第 4 步 customer-settlement -> customer-management-04
   *
   * 注意：序号必须基于 task.steps 的完整原始顺序，不能基于不同 experience
   * 过滤后的 currentSteps，否则 new/similar/expert 会产生不同的后端 stepId。
   */
  function getRemoteStepId(taskId: string, localStepId: string) {
    if (!taskId || !localStepId) {
      return ''
    }

    const task = edition.value.tasks.find(item => item.id === taskId)
    if (!task) {
      return localStepId
    }

    // 兼容调用方已经传入稳定后端 stepId 的情况。
    const remotePrefix = `${taskId}-`
    if (localStepId.startsWith(remotePrefix)) {
      const sequenceText = localStepId.slice(remotePrefix.length)
      const sequence = Number(sequenceText)
      if (/^\d+$/.test(sequenceText) && sequence >= 1 && sequence <= task.steps.length) {
        return `${taskId}-${String(sequence).padStart(2, '0')}`
      }
    }

    const stepIndex = task.steps.findIndex(step => step.id === localStepId)
    if (stepIndex < 0) {
      // 找不到配置时保留原值，避免前端因为单个异常步骤直接丢失整个 cursor。
      // 正常教程配置都应该走到上面的稳定 ID 转换。
      return localStepId
    }

    return `${taskId}-${String(stepIndex + 1).padStart(2, '0')}`
  }

  /**
   * 把 GET 返回的稳定步骤 ID 转回前端原始 step.id。
   *
   * 同时兼容历史数据：如果后端以前直接保存过 top-nav/customer-table
   * 这种前端 step.id，也可以直接恢复。
   */
  function getLocalStepId(taskId: string, remoteStepId: string) {
    if (!taskId || !remoteStepId) {
      return ''
    }

    const task = edition.value.tasks.find(item => item.id === taskId)
    if (!task) {
      return remoteStepId
    }

    // 兼容历史服务端直接保存前端 step.id 的数据。
    if (task.steps.some(step => step.id === remoteStepId)) {
      return remoteStepId
    }

    const remotePrefix = `${taskId}-`
    if (!remoteStepId.startsWith(remotePrefix)) {
      return remoteStepId
    }

    const sequenceText = remoteStepId.slice(remotePrefix.length)
    if (!/^\d+$/.test(sequenceText)) {
      return remoteStepId
    }

    const stepIndex = Number(sequenceText) - 1
    if (stepIndex < 0 || stepIndex >= task.steps.length) {
      return remoteStepId
    }

    return task.steps[stepIndex]?.id || remoteStepId
  }

  function applyRemoteStepSummary(data: Partial<RemoteOnboardingState> | null | undefined, replaceMissing = false) {
    if (!data || typeof data !== 'object') {
      return
    }

    // 当前后端按请求格式原样保存/返回 stepChanges；同时兼容已有数据中的 stepStates。
    const hasStepChanges = Array.isArray(data.stepChanges)
    const hasStepStates = Array.isArray(data.stepStates)
    if (hasStepChanges || hasStepStates || replaceMissing) {
      const stepStates: Array<RemoteStepState | PatchStepChange> = hasStepChanges
        ? data.stepChanges!
        : (hasStepStates ? data.stepStates! : [])
      if (hasStepChanges) {
        remoteStepChanges.value = data.stepChanges!.map(change => ({ ...change }))
      }
      else if (hasStepStates) {
        const occurredAt = new Date().toISOString()
        remoteStepChanges.value = data.stepStates!.map(step => ({
          taskId: step.taskId,
          stepId: step.stepId,
          status: step.status,
          source: 'tutorial_confirm',
          clientOccurredAt: step.completedAt || step.updatedAt || occurredAt,
        }))
      }
      else {
        remoteStepChanges.value = []
      }
      const toLocalStepIds = (targetStatus: RemoteStepStatus) => [
        ...new Set(stepStates
          .filter(item => item.status === targetStatus)
          .map(item => getLocalStepId(item.taskId, item.stepId))
          .filter((stepId): stepId is string => Boolean(stepId))),
      ]

      // stepChanges 是完整快照，直接恢复前端累计状态。
      completedStepIds.value = toLocalStepIds('completed')
      skippedStepIds.value = toLocalStepIds('skipped')
      deferredStepIds.value = toLocalStepIds('deferred')
    }

    if (Array.isArray(data.completedTaskIds)) {
      completedTaskIds.value = [...new Set(data.completedTaskIds)]
    }
    else if (replaceMissing) {
      completedTaskIds.value = []
    }

    // completedTaskIds 即使后端暂未显式返回，也可以根据累计 stepStates 做安全补全。
    // 只有当前经验路线的全部有效步骤都 completed，才把任务标为已完成。
    const derivedCompletedTaskIds = tasks.value
      .filter((task) => {
        const taskSteps = stepsForExperience(task, experience.value)
        return taskSteps.length > 0 && taskSteps.every(step => completedStepIds.value.includes(step.id))
      })
      .map(task => task.id)

    completedTaskIds.value = [...new Set([
      ...completedTaskIds.value,
      ...derivedCompletedTaskIds,
    ])]
  }

  function currentRouteKey() {
    // 按后端实际 PATCH JSON 保存当前 path，不带 query/hash。
    // 示例：/tenant/clients。
    return typeof window === 'undefined' ? null : (window.location.pathname || null)
  }

  function buildPatchCursor(): PatchCursorState | null {
    const localStepId = currentStep.value?.id || ''
    if (!activeTaskId.value || !localStepId) {
      return null
    }

    const remoteStepId = getRemoteStepId(activeTaskId.value, localStepId)
    if (!remoteStepId) {
      return null
    }

    return {
      // GET 返回 taskId/stepId；PATCH 必须使用 currentTaskId/currentStepId。
      // currentStepId 同时转换成后端稳定步骤 ID，例如 customer-management-05。
      currentTaskId: activeTaskId.value,
      currentStepId: remoteStepId,
      status: status.value === 'running' ? 'active' : status.value,
      resumeOnLoad: true,
      lastRouteKey: currentRouteKey(),
      pauseReason: status.value === 'paused' ? 'user-paused' : null,
    }
  }

  function getPatchSetupExperience(): PatchSetupExperience | null {
    // 后端 PATCH 明确只允许 new / similar / expert。
    // quick 是前端的快捷模式标记，不直接作为 setup.experience 提交；快捷开始统一按 similar 保存。
    const selectedExperience = initialized.value ? experience.value : setupExperience.value
    if (selectedExperience === 'new' || selectedExperience === 'similar' || selectedExperience === 'expert') {
      return selectedExperience
    }
    return null
  }

  function buildPatchSetup(): PatchSetupState | null {
    const selectedExperience = getPatchSetupExperience()

    // 关键规则：用户还没有真正选择经验时，绝不能把 experience:null 发给后端。
    // 此时返回 null，调用方应跳过 setup PATCH；这样首次仅看到欢迎页、
    // 或只点击“开始设置”进入经验选择页，都不会创建错误的服务端进度。
    if (!selectedExperience) {
      return null
    }

    // setupStep 是前端页面数组下标（0~3），不能用它判断是否完成。
    // 第四步刚显示时 setupStep=3 但 complete=false；只有第四步点击“开始学习”后 initialized=true。
    const setupFinished = initialized.value
    const selectedGoals = setupGoals.value.length ? [...setupGoals.value] : [...goals.value]

    return {
      complete: setupFinished,
      // 接口 setup.step 使用正常业务序号 1~4；前端内部仍保持 0~3，避免 UI 数组越界。
      step: Math.max(1, Math.min(4, setupStep.value + 1)),
      experience: selectedExperience,
      // goals 直接使用当前前端配置值；后端已删除旧固定枚举，不做 goal_xxx 映射。
      goals: selectedGoals,
      mode: 'full',
      laterUntil: null,
      reminderDisabled: false,
    }
  }

  function makeStepChange(
    taskId: string,
    localStepId: string,
    stepStatus: PatchStepStatus,
  ): PatchStepChange | null {
    if (!taskId || !localStepId) {
      return null
    }

    const remoteStepId = getRemoteStepId(taskId, localStepId)
    if (!remoteStepId) {
      return null
    }

    return {
      taskId,
      // PATCH stepChanges.stepId 使用后端稳定 ID，例如 customer-management-04。
      stepId: remoteStepId,
      status: stepStatus,
      source: 'tutorial_confirm',
      clientOccurredAt: new Date().toISOString(),
    }
  }

  /**
   * 生成当前账号全部步骤状态快照。
   *
   * 当前后端会按 PATCH 请求原样保存，因此 stepChanges 必须累计提交，不能只发送
   * 本次变化。recentChanges 用来保留本次用户操作的发生时间，其余已有状态使用
   * 当前快照时间补齐。
   */
  function buildCumulativeStepChanges(recentChanges: PatchStepChange[] = []) {
    const occurredAt = new Date().toISOString()
    const recentChangeMap = new Map(recentChanges.map(change => [
      `${change.taskId}:${change.stepId}:${change.status}`,
      change,
    ]))
    const previousChangeMap = new Map(remoteStepChanges.value.map(change => [
      `${change.taskId}:${change.stepId}:${change.status}`,
      change,
    ]))
    const changes: PatchStepChange[] = []

    edition.value.tasks.forEach((task) => {
      task.steps.forEach((step) => {
        let stepStatus: PatchStepStatus | null = null
        if (completedStepIds.value.includes(step.id)) {
          stepStatus = 'completed'
        }
        else if (skippedStepIds.value.includes(step.id)) {
          stepStatus = 'skipped'
        }
        else if (deferredStepIds.value.includes(step.id)) {
          stepStatus = 'deferred'
        }
        else if (activeTaskId.value === task.id && currentStep.value?.id === step.id) {
          stepStatus = 'in_progress'
        }

        if (!stepStatus) {
          return
        }

        const remoteStepId = getRemoteStepId(task.id, step.id)
        const recentChange = recentChangeMap.get(`${task.id}:${remoteStepId}:${stepStatus}`)
        const previousChange = previousChangeMap.get(`${task.id}:${remoteStepId}:${stepStatus}`)
        changes.push(recentChange || previousChange || {
          taskId: task.id,
          stepId: remoteStepId,
          status: stepStatus,
          source: 'tutorial_confirm',
          clientOccurredAt: occurredAt,
        })
      })
    })

    return changes
  }

  function updateRemoteTokensFromResponse(data: any) {
    if (!data || typeof data !== 'object') {
      return
    }
    if (typeof data.exists === 'boolean') {
      remoteExists.value = data.exists
    }
    else {
      // PATCH 成功本身就说明服务端已经有该账号的进度记录。
      remoteExists.value = true
    }
    if (Number.isFinite(Number(data.revision))) {
      remoteRevision.value = Number(data.revision)
    }
    if (Number.isFinite(Number(data.resetEpoch))) {
      remoteResetEpoch.value = Number(data.resetEpoch)
    }
    remoteStateLoaded.value = true
    remoteStateLoadFailed.value = false
  }

  async function refreshRemoteVersionTokens() {
    const response = await apiUser.getOnboardingProgress()
    const data = response?.data as RemoteOnboardingState | undefined
    if (!data || typeof data.exists !== 'boolean') {
      throw new Error('无法刷新新手引导 revision/resetEpoch')
    }
    updateRemoteTokensFromResponse(data)
  }

  async function sendRemotePatch(snapshot: RemotePatchSnapshot) {
    remoteSyncing.value = true
    remoteSyncFailed.value = false
    let mutationId = createMutationId()

    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const response = await apiUser.patchOnboardingProgress({
          mutationId,
          // baseRevision / resetEpoch 永远使用最近一次 GET 或 PATCH 成功返回的最新值，禁止写死。
          baseRevision: remoteRevision.value,
          resetEpoch: remoteResetEpoch.value,
          contentVersion: edition.value.guideVersion,
          schemaVersion: 7,
          stepChanges: snapshot.stepChanges,
          cursor: snapshot.cursor,
          setup: snapshot.setup,
          migration: null,
        })
        const responseData = response?.data as RemoteOnboardingState | undefined
        updateRemoteTokensFromResponse(responseData)
        remoteStepChanges.value = snapshot.stepChanges.map(change => ({ ...change }))

        // PATCH 响应可能只返回本次变化，不能用它替换本地累计步骤和任务状态。
        // 本地状态已在用户点击时完成乐观更新；这里只合并后端明确确认的任务 ID。
        if (Array.isArray(responseData?.completedTaskIds)) {
          completedTaskIds.value = [...new Set([
            ...completedTaskIds.value,
            ...responseData.completedTaskIds,
          ])]
        }

        remoteSyncFailed.value = false
        return true
      }
      catch (error: any) {
        const httpStatus = Number(error?.response?.status || error?.status)
        if (httpStatus === 409 && attempt === 0) {
          // revision 冲突时只刷新并发控制字段，不覆盖用户当前页面中的本地操作状态。
          // 随后使用新的 mutationId 和最新 revision 再重试一次当前操作。
          try {
            await refreshRemoteVersionTokens()
            mutationId = createMutationId()
            continue
          }
          catch (refreshError) {
            console.warn('[Onboarding] 刷新服务端进度版本失败。', refreshError)
          }
        }
        remoteSyncFailed.value = true
        console.warn('[Onboarding] 保存服务端新手引导进度失败，本地界面继续可用。', error)
        return false
      }
    }

    return false
  }

  /**
   * 调用独立重置接口清空服务端路线、任务、步骤和游标。
   * 成功响应中的 revision/resetEpoch 是后续 PATCH 必须使用的新版本令牌。
   */
  async function sendRemoteFullProgressReset() {
    remoteSyncing.value = true
    remoteSyncFailed.value = false
    let mutationId = createMutationId()

    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const response = await apiUser.resetOnboardingProgress({
          mutationId,
          baseRevision: remoteRevision.value,
          currentResetEpoch: remoteResetEpoch.value,
          scope: 'full_progress',
          reason: 'user_requested',
        })
        const responseData = response?.data as RemoteOnboardingState | undefined

        updateRemoteTokensFromResponse(responseData)
        applyRemoteStepSummary(responseData, true)
        clearTaskProgressAfterRestartReset()
        remoteExists.value = true
        remoteSyncFailed.value = false
        remoteSyncing.value = false
        return true
      }
      catch (error: any) {
        const httpStatus = Number(error?.response?.status || error?.status)
        if (httpStatus === 409 && attempt === 0) {
          try {
            await refreshRemoteVersionTokens()
            mutationId = createMutationId()
            continue
          }
          catch (refreshError) {
            console.warn('[Onboarding] 重置前刷新服务端 revision/resetEpoch 失败。', refreshError)
          }
        }

        remoteSyncFailed.value = true
        remoteSyncing.value = false
        console.warn('[Onboarding] 清空服务端新手引导进度失败。', error)
        return false
      }
    }

    remoteSyncing.value = false
    return false
  }

  /** 清空本地任务学习进度，但保留欢迎页中重新选择的 experience/goals。 */
  function clearTaskProgressAfterRestartReset() {
    completedTaskIds.value = []
    completedStepIds.value = []
    skippedStepIds.value = []
    deferredStepIds.value = []
    remoteStepChanges.value = []
    stepDataStatuses.value = {}
    activeTaskId.value = ''
    activeStepIndex.value = 0
    status.value = 'idle'
    targetMissing.value = false
    showAllTasks.value = false
  }

  function queueRemotePatch(snapshot: RemotePatchSnapshot): Promise<void> {
    // 只在队列中保存本次操作。等前一条请求完成、真正发送时，再根据最新本地状态
    // 生成累计快照，避免较早入队的快照覆盖之后已经完成的任务。
    const queuedSnapshot: RemotePatchSnapshot = {
      ...snapshot,
      stepChanges: snapshot.stepChanges.map(change => ({ ...change })),
      cursor: snapshot.cursor ? { ...snapshot.cursor } : null,
      setup: snapshot.setup
        ? { ...snapshot.setup, goals: [...snapshot.setup.goals] }
        : null,
    }

    // 串行发送 PATCH，保证后一条请求拿到前一条成功后的最新 revision。
    remotePatchChain = remotePatchChain
      .then(async () => {
        const cumulativeSnapshot: RemotePatchSnapshot = {
          ...queuedSnapshot,
          stepChanges: buildCumulativeStepChanges(queuedSnapshot.stepChanges),
        }
        await sendRemotePatch(cumulativeSnapshot)
      })
      .catch((error) => {
        remoteSyncFailed.value = true
        console.warn('[Onboarding] 新手引导进度同步队列异常。', error)
      })
      .finally(() => {
        remoteSyncing.value = false
      })

    return remotePatchChain
  }

  function syncSetupProgress() {
    // “重新设置”期间 setupExperience/setupGoals 只是本地草稿。
    // 用户最终确认前不能改写后端原有 setup 和任务进度。
    if (isRestartSetupFlow()) {
      return
    }

    const setup = buildPatchSetup()

    // 用户尚未选择经验时不发送 PATCH；第一次有效保存从选择经验后开始。
    if (!setup) {
      return
    }

    queueRemotePatch({
      stepChanges: [],
      cursor: buildPatchCursor(),
      setup,
    })
  }

  function syncCursorProgress() {
    queueRemotePatch({
      stepChanges: [],
      cursor: buildPatchCursor(),
      setup: null,
    })
  }

  function getPersistedState(): PersistedOnboardingState | null {
    try {
      const legacyKeys = edition.value.id === 'enterprise'
        ? [[
            'surveysaas.onboarding',
            window.location.hostname,
            tenantId.value || 'tenant',
            account.value || 'user',
            edition.value.id,
            'enterprise-1.5.0',
          ].join(':')]
        : []
      const raw = [storageKey.value, ...legacyKeys]
        .map(key => localStorage.getItem(key))
        .find(Boolean)
      return raw ? JSON.parse(raw) : null
    }
    catch {
      return null
    }
  }

  function save() {
    try {
      localStorage.setItem(storageKey.value, JSON.stringify({
        experience: experience.value,
        goals: goals.value,
        activeTaskId: activeTaskId.value,
        activeStepIndex: activeStepIndex.value,
        completedTaskIds: completedTaskIds.value,
        completedStepIds: completedStepIds.value,
        skippedStepIds: skippedStepIds.value,
        deferredStepIds: deferredStepIds.value,
        initialized: initialized.value,
        laterUntil: laterUntil.value,
        status: status.value,
        launcherPosition: launcherPosition.value,
        launcherDismissed: launcherDismissed.value,
        resetProgressAfterSetupConfirm: resetProgressAfterSetupConfirm.value,
      }))
    }
    catch {
      // The guide remains usable in memory when browser storage is unavailable.
    }
  }

  function sanitizeState() {
    const taskIds = new Set(tasks.value.map(task => task.id))
    const stepIds = new Set(tasks.value.flatMap(task => task.steps.map(step => step.id)))
    const goalIds = new Set(edition.value.goals.map(goal => goal.id))
    completedTaskIds.value = completedTaskIds.value.filter(taskId => taskIds.has(taskId))
    completedStepIds.value = completedStepIds.value.filter(stepId => stepIds.has(stepId))
    skippedStepIds.value = skippedStepIds.value.filter(stepId => stepIds.has(stepId))
    deferredStepIds.value = deferredStepIds.value.filter(stepId => stepIds.has(stepId))
    goals.value = goals.value.filter(goalId => goalIds.has(goalId))

    if (!taskIds.has(activeTaskId.value)) {
      activeTaskId.value = ''
      activeStepIndex.value = 0
      status.value = 'idle'
    }
    if (activeStepIndex.value >= currentSteps.value.length) {
      activeStepIndex.value = 0
    }
  }

  function restore(stateToRestore: PersistedOnboardingState) {
    experience.value = stateToRestore.experience || 'new'
    goals.value = Array.isArray(stateToRestore.goals) ? stateToRestore.goals : ['project']
    activeTaskId.value = stateToRestore.activeTaskId || ''
    activeStepIndex.value = Number.isInteger(stateToRestore.activeStepIndex)
      ? Number(stateToRestore.activeStepIndex)
      : 0
    completedTaskIds.value = Array.isArray(stateToRestore.completedTaskIds)
      ? stateToRestore.completedTaskIds
      : []
    completedStepIds.value = Array.isArray(stateToRestore.completedStepIds)
      ? stateToRestore.completedStepIds
      : []
    skippedStepIds.value = Array.isArray(stateToRestore.skippedStepIds)
      ? stateToRestore.skippedStepIds
      : []
    deferredStepIds.value = Array.isArray(stateToRestore.deferredStepIds)
      ? stateToRestore.deferredStepIds
      : []
    initialized.value = Boolean(stateToRestore.initialized)
    laterUntil.value = Number(stateToRestore.laterUntil) || 0
    status.value = stateToRestore.status === 'running'
      ? 'paused'
      : (stateToRestore.status || 'idle')
    launcherPosition.value = {
      edge: stateToRestore.launcherPosition?.edge === 'left' ? 'left' : 'right',
      yRatio: stateToRestore.launcherPosition?.yRatio !== null
        && stateToRestore.launcherPosition?.yRatio !== undefined
        && Number.isFinite(Number(stateToRestore.launcherPosition.yRatio))
        ? Math.max(0, Math.min(1, Number(stateToRestore.launcherPosition?.yRatio)))
        : null,
    }
    launcherDismissed.value = Boolean(stateToRestore.launcherDismissed)
    resetProgressAfterSetupConfirm.value = Boolean(stateToRestore.resetProgressAfterSetupConfirm)
    sanitizeState()
  }

  function initialize(context: OnboardingContext) {
    const nextSignature = `${context.tenantId}:${context.account}:${context.versionId}`
    if (nextSignature === contextSignature.value) {
      return
    }

    versionId.value = String(context.versionId || '3').trim()
    account.value = context.account || ''
    tenantId.value = context.tenantId || ''
    contextSignature.value = nextSignature
    resetEnterpriseDataContext()
    welcomeVisible.value = false
    taskCenterVisible.value = false
    status.value = 'idle'
    experience.value = 'new'
    goals.value = ['project']
    setupStep.value = 0
    setupExperience.value = ''
    setupGoals.value = []
    showAllTasks.value = false
    activeTaskId.value = ''
    activeStepIndex.value = 0
    completedTaskIds.value = []
    completedStepIds.value = []
    skippedStepIds.value = []
    deferredStepIds.value = []
    stepDataStatuses.value = {}
    runtimeUnavailableStepIds.value = []
    accessibleTaskIds.value = null
    workspaceRoute.value = ''
    initialized.value = false
    laterUntil.value = 0
    targetMissing.value = false
    launcherPosition.value = { edge: 'right', yRatio: null }
    launcherDismissed.value = false
    resetProgressAfterSetupConfirm.value = false
    restartSetupSessionActive = false
    setupCompletionInFlight = false

    // 切换账号/租户/版本后重新查询服务端，不复用上一个账号的 exists/revision。
    remoteExists.value = null
    remoteStateLoaded.value = false
    remoteStateLoadFailed.value = false
    remoteSyncing.value = false
    remoteSyncFailed.value = false
    remoteRevision.value = 0
    remoteResetEpoch.value = 0
    remoteStepChanges.value = []
    autoWelcomeOpened.value = false
    remoteLoadPromise = null
    remotePatchChain = Promise.resolve()

    const persistedState = getPersistedState()
    if (persistedState) {
      restore(persistedState)
      save()
    }
  }

  // 首次引导必须由全局引导组件在系统主页面完成渲染后触发。
  // 三个版本完全统一：自动弹出欢迎页只认后端 GET 的 exists。
  // exists=false 才自动弹；exists=true 或 GET 失败都不自动弹。
  function openWelcomeWhenSystemReady() {
    if (
      !remoteStateLoaded.value
      || remoteStateLoadFailed.value
      || remoteExists.value !== false
      || welcomeVisible.value
      || autoWelcomeOpened.value
    ) {
      return false
    }
    autoWelcomeOpened.value = true
    openWelcome(0)
    return true
  }

  function clearSession() {
    welcomeVisible.value = false
    taskCenterVisible.value = false
    status.value = 'idle'
    activeTaskId.value = ''
    activeStepIndex.value = 0
    targetMissing.value = false
    stepDataStatuses.value = {}
    runtimeUnavailableStepIds.value = []
    contextSignature.value = ''
    accessibleTaskIds.value = null
    workspaceRoute.value = ''
    remoteExists.value = null
    remoteStateLoaded.value = false
    remoteStateLoadFailed.value = false
    remoteSyncing.value = false
    remoteSyncFailed.value = false
    remoteRevision.value = 0
    remoteResetEpoch.value = 0
    remoteStepChanges.value = []
    autoWelcomeOpened.value = false
    remoteLoadPromise = null
    remotePatchChain = Promise.resolve()
    resetProgressAfterSetupConfirm.value = false
    restartSetupSessionActive = false
    setupCompletionInFlight = false
  }

  function setAccessibleTaskIds(taskIds: string[]) {
    accessibleTaskIds.value = [...new Set(taskIds)]
    sanitizeState()
  }

  function setWorkspaceRoute(path: string) {
    workspaceRoute.value = path
  }

  function openWelcome(step = 0) {
    setupStep.value = Math.max(0, Math.min(3, step))

    if (initialized.value) {
      // 已完成设置后点击“重新设置”时，用正式状态回填欢迎页。
      setupExperience.value = experience.value
      setupGoals.value = [...goals.value]
    }
    // initialized=false 时不要清空 setupExperience/setupGoals：
    // GET 可能已经恢复了用户停在第 2/3/4 步的未完成设置，悬浮按钮再次打开时必须保留这些选择。

    welcomeVisible.value = true
    taskCenterVisible.value = false
  }

  function deferWelcome() {
    // 三个版本统一：“稍后提醒/关闭欢迎页”不属于真正操作教程，不调用 PATCH。
    // 不写 24 小时本地延迟；只要服务端仍 exists=false，刷新或下次登录仍会自动弹。
    if (isRestartSetupFlow()) {
      // 退出“重新设置”即放弃草稿，恢复已正式保存的设置和进度。
      resetProgressAfterSetupConfirm.value = false
      restartSetupSessionActive = false
      setupExperience.value = initialized.value ? experience.value : ''
      setupGoals.value = initialized.value ? [...goals.value] : []
      setupStep.value = initialized.value ? 3 : 0
    }
    laterUntil.value = 0
    welcomeVisible.value = false
    save()
  }

  function nextSetup() {
    // 第 0 步“开始设置”只进入经验选择页。
    // 此时用户还没有选择 experience，不发送 PATCH，避免 experience=null。
    if (setupStep.value === 0) {
      setupStep.value = 1
      save()
      return
    }

    // 第二个页面（setupStep=1）：选择使用经验后进入第三个“目标选择”页面。
    // 前端进入 setupStep=2 后，PATCH 会提交 complete=false + setup.step=3。
    if (setupStep.value === 1) {
      if (!setupExperience.value) {
        return
      }

      if (!isRestartSetupFlow()) {
        initialized.value = false
      }
      setupStep.value = 2
      save()
      syncSetupProgress()
      return
    }

    // 第 3 个页面（setupStep=2）：用户选择目标并点击“生成路线”后，只进入第 4 个页面。
    // 此时第四步只是刚显示，设置尚未真正完成：initialized=false / complete=false。
    // PATCH 时前端 setupStep=3 会转换成接口 setup.step=4。
    if (setupStep.value === 2) {
      if (!setupGoals.value.length) {
        return
      }
      if (setupExperience.value !== 'new'
        && setupExperience.value !== 'similar'
        && setupExperience.value !== 'expert') {
        setupStep.value = 1
        save()
        return
      }

      // “重新设置”只更新欢迎页草稿；原正式设置在第四步确认成功前保持不变。
      if (!isRestartSetupFlow()) {
        experience.value = setupExperience.value
        goals.value = [...setupGoals.value]
        initialized.value = false
      }
      setupStep.value = 3
      laterUntil.value = 0
      save()

      // 进入第四步时保存：setup.step=4 + complete=false。
      // 只有用户在第四步点击“开始学习”后才会再次 PATCH complete=true。
      syncSetupProgress()
    }
  }

  function previousSetup() {
    // 从第四个页面（setupStep=3）点击“调整”回到第三个页面（setupStep=2）。
    // setup.step 会从 4 转为 3；只要重新进入设置流程，就保持 complete=false。
    if (setupStep.value === 3 && !isRestartSetupFlow()) {
      initialized.value = false
    }

    setupStep.value = Math.max(0, setupStep.value - 1)
    save()

    // 返回上一步时，只有已经选择过合法 experience 才同步 setup。
    // 未选择经验时 buildPatchSetup() 会返回 null，不会发出非法请求。
    syncSetupProgress()
  }

  async function completeSetup(openTasksOnly = false) {
    // 只有第四个页面（前端 setupStep=3）点击最终按钮才算欢迎设置真正完成。
    // 接口会提交 setup.step=4 + complete=true。
    if (setupStep.value !== 3 || setupCompletionInFlight) {
      return
    }
    if (setupExperience.value !== 'new'
      && setupExperience.value !== 'similar'
      && setupExperience.value !== 'expert') {
      console.warn('[Onboarding] 完成新手设置失败：尚未选择有效的使用经验。')
      if (!isRestartSetupFlow()) {
        initialized.value = false
      }
      setupStep.value = 1
      save()
      return
    }
    if (!setupGoals.value.length) {
      console.warn('[Onboarding] 完成新手设置失败：尚未选择学习目标。')
      if (!isRestartSetupFlow()) {
        initialized.value = false
      }
      setupStep.value = 2
      save()
      return
    }

    setupCompletionInFlight = true
    try {
      // 先等待之前已入队的 PATCH，确保本次最终提交使用最新 revision。
      await remotePatchChain

      const isRestartCommit = isRestartSetupFlow()
      let resetAppliedThisCommit = false
      const previousState = {
        experience: experience.value,
        goals: [...goals.value],
        initialized: initialized.value,
        laterUntil: laterUntil.value,
        showAllTasks: showAllTasks.value,
        activeTaskId: activeTaskId.value,
        activeStepIndex: activeStepIndex.value,
        completedTaskIds: [...completedTaskIds.value],
        completedStepIds: [...completedStepIds.value],
        skippedStepIds: [...skippedStepIds.value],
        deferredStepIds: [...deferredStepIds.value],
        remoteStepChanges: remoteStepChanges.value.map(change => ({ ...change })),
        stepDataStatuses: { ...stepDataStatuses.value },
        status: status.value,
        targetMissing: targetMissing.value,
        taskCenterVisible: taskCenterVisible.value,
      }

      if (isRestartCommit) {
        // 独立 reset 必须先成功；后续 PATCH 才能使用它返回的新 revision/resetEpoch。
        const resetSucceeded = await sendRemoteFullProgressReset()
        if (!resetSucceeded) {
          welcomeVisible.value = true
          return
        }
        resetAppliedThisCommit = true
        resetProgressAfterSetupConfirm.value = false
        restartSetupSessionActive = false

        // reset 已不可逆地清空服务端，先保存对应的本地空状态，避免页面异常时恢复旧进度。
        experience.value = setupExperience.value
        goals.value = [...setupGoals.value]
        initialized.value = false
        setupStep.value = 3
        laterUntil.value = 0
        welcomeVisible.value = true
        save()
      }

      // 先在内存中构造最终状态，欢迎页仍保持打开；只有 PATCH 成功才真正切换界面。
      experience.value = setupExperience.value
      goals.value = [...setupGoals.value]
      initialized.value = true
      setupStep.value = 3
      laterUntil.value = 0
      showAllTasks.value = false

      const firstTaskId = recommendedIds.value
        .find(taskId => !completedTaskIds.value.includes(taskId)) || recommendedIds.value[0]
      const shouldOpenTaskCenter = openTasksOnly || experience.value === 'expert' || !firstTaskId

      if (shouldOpenTaskCenter) {
        activeTaskId.value = ''
        activeStepIndex.value = 0
        status.value = 'idle'
        targetMissing.value = false
      }
      else {
        const firstTask = tasks.value.find(task => task.id === firstTaskId) || null
        const firstTaskSteps = stepsForExperience(firstTask, experience.value)
          .filter(step => !runtimeUnavailableStepIds.value.includes(step.id))
        const nextStepIndex = firstTaskSteps.findIndex(step => (
          !completedStepIds.value.includes(step.id) && !skippedStepIds.value.includes(step.id)
        ))
        activeTaskId.value = firstTaskId
        activeStepIndex.value = nextStepIndex >= 0 ? nextStepIndex : 0
        status.value = 'running'
        targetMissing.value = false
        taskCenterVisible.value = false
      }

      const finalSnapshot: RemotePatchSnapshot = {
        // 重设时这里是“新第一步 in_progress”或空数组，不再包含任何旧任务步骤。
        stepChanges: buildCumulativeStepChanges(),
        cursor: buildPatchCursor(),
        setup: buildPatchSetup(),
      }
      const patchSucceeded = await sendRemotePatch(finalSnapshot)
      remoteSyncing.value = false

      if (!patchSucceeded) {
        if (resetAppliedThisCommit) {
          // reset 已成功，旧进度不能再恢复；保持空状态并允许用户直接重试最终 PATCH。
          clearTaskProgressAfterRestartReset()
          experience.value = setupExperience.value
          goals.value = [...setupGoals.value]
          initialized.value = false
          setupStep.value = 3
          laterUntil.value = 0
          taskCenterVisible.value = false
          welcomeVisible.value = true
          save()
          return
        }

        // 普通首次设置保存失败时恢复原状态，欢迎页保留在第四步供用户重试。
        experience.value = previousState.experience
        goals.value = previousState.goals
        initialized.value = previousState.initialized
        laterUntil.value = previousState.laterUntil
        showAllTasks.value = previousState.showAllTasks
        activeTaskId.value = previousState.activeTaskId
        activeStepIndex.value = previousState.activeStepIndex
        completedTaskIds.value = previousState.completedTaskIds
        completedStepIds.value = previousState.completedStepIds
        skippedStepIds.value = previousState.skippedStepIds
        deferredStepIds.value = previousState.deferredStepIds
        remoteStepChanges.value = previousState.remoteStepChanges
        stepDataStatuses.value = previousState.stepDataStatuses
        status.value = previousState.status
        targetMissing.value = previousState.targetMissing
        taskCenterVisible.value = previousState.taskCenterVisible
        welcomeVisible.value = true
        save()
        return
      }

      if (isRestartCommit) {
        // 不采用响应中可能残留的历史汇总，只认本次覆盖提交的新步骤快照。
        applyRemoteStepSummary({
          stepChanges: finalSnapshot.stepChanges,
          completedTaskIds: [],
        }, true)
      }

      resetProgressAfterSetupConfirm.value = false
      restartSetupSessionActive = false
      // 已经保存了新的 setup/第一步，当前账号仍然属于“存在引导数据”。
      remoteExists.value = true
      welcomeVisible.value = false
      taskCenterVisible.value = shouldOpenTaskCenter
      save()
    }
    finally {
      remoteSyncing.value = false
      setupCompletionInFlight = false
    }
  }

  function openTaskCenter(showAll = false) {
    welcomeVisible.value = false
    if (showAll) {
      showAllTasks.value = true
    }
    taskCenterVisible.value = true
  }

  function closeTaskCenter() {
    taskCenterVisible.value = false
  }

  function toggleAllTasks() {
    showAllTasks.value = !showAllTasks.value
  }

  function taskStatus(taskId: string) {
    if (completedTaskIds.value.includes(taskId)) {
      return 'completed'
    }
    if (activeTaskId.value === taskId && status.value !== 'idle') {
      return status.value === 'paused' ? 'paused' : 'running'
    }
    const task = tasks.value.find(item => item.id === taskId)
    const taskSteps = stepsForExperience(task || null, experience.value)
    if (taskSteps.some(step => skippedStepIds.value.includes(step.id))) {
      return 'partial'
    }
    if (taskSteps.some(step => deferredStepIds.value.includes(step.id))) {
      return 'deferred'
    }
    const dataStatuses = taskSteps.map(step => stepDataStatuses.value[step.id]).filter(Boolean)
    if (dataStatuses.includes('empty-prerequisite')) {
      return 'deferred'
    }
    if (dataStatuses.includes('empty-creatable')) {
      return 'actionable'
    }
    const hasProgress = taskSteps.some(step => completedStepIds.value.includes(step.id))
    return hasProgress ? 'partial' : 'idle'
  }

  function startTask(taskId: string, shouldSync = true) {
    const task = tasks.value.find(item => item.id === taskId)
    if (!task) {
      return
    }
    const taskSteps = stepsForExperience(task, experience.value)
    const nextStepIndex = taskSteps.findIndex(step => (
      !completedStepIds.value.includes(step.id) && !skippedStepIds.value.includes(step.id)
    ))
    activeTaskId.value = taskId
    activeStepIndex.value = nextStepIndex >= 0 ? nextStepIndex : 0
    targetMissing.value = false
    taskCenterVisible.value = false
    status.value = 'running'
    save()

    if (shouldSync) {
      // 进入某一步时只保存 cursor：
      // currentTaskId/currentStepId/status=active 已能完整表示“当前正在学习哪一步”。
      // queueRemotePatch 会把当前步骤作为 in_progress 放入累计 stepChanges。
      queueRemotePatch({
        stepChanges: [],
        cursor: buildPatchCursor(),
        setup: null,
      })
    }
  }

  function markCurrentStepCompleted() {
    const stepId = currentStep.value?.id
    if (stepId && !completedStepIds.value.includes(stepId)) {
      completedStepIds.value.push(stepId)
    }
    if (stepId) {
      skippedStepIds.value = skippedStepIds.value.filter(id => id !== stepId)
      deferredStepIds.value = deferredStepIds.value.filter(id => id !== stepId)
    }
  }

  function next(options: { deferred?: boolean } = {}): Promise<void> {
    const taskId = activeTaskId.value
    const stepId = currentStep.value?.id || ''
    const stepStatus: PatchStepStatus = options.deferred ? 'deferred' : 'completed'

    if (options.deferred && stepId) {
      completedStepIds.value = completedStepIds.value.filter(id => id !== stepId)
      skippedStepIds.value = skippedStepIds.value.filter(id => id !== stepId)
      if (!deferredStepIds.value.includes(stepId)) {
        deferredStepIds.value.push(stepId)
      }
    }
    else {
      markCurrentStepCompleted()
    }

    const stepChange = makeStepChange(taskId, stepId, stepStatus)
    if (activeStepIndex.value < currentSteps.value.length - 1) {
      activeStepIndex.value += 1
      targetMissing.value = false
      save()
      // 一次 PATCH 同时保存“当前步骤状态 + 下一步 cursor”，避免两者不同步。
      return queueRemotePatch({
        stepChanges: stepChange ? [stepChange] : [],
        cursor: buildPatchCursor(),
        setup: null,
      })
    }

    completeTask(false)
    return queueRemotePatch({
      stepChanges: stepChange ? [stepChange] : [],
      cursor: null,
      setup: null,
    })
  }

  function previous() {
    if (activeStepIndex.value > 0) {
      activeStepIndex.value -= 1
      targetMissing.value = false
      save()
      // 返回上一步只更新 cursor，不取消已经 completed 的步骤。
      syncCursorProgress()
    }
  }

  function completeTask(shouldSync = true) {
    const taskId = activeTaskId.value
    const taskStepIds = currentSteps.value.map(step => step.id)
    const hasUnresolvedSteps = taskStepIds.some(stepId => (
      skippedStepIds.value.includes(stepId) || deferredStepIds.value.includes(stepId)
    ))
    if (taskId && !hasUnresolvedSteps && !completedTaskIds.value.includes(taskId)) {
      completedTaskIds.value.push(taskId)
    }
    if (taskId && hasUnresolvedSteps) {
      completedTaskIds.value = completedTaskIds.value.filter(id => id !== taskId)
    }
    status.value = 'idle'
    targetMissing.value = false
    taskCenterVisible.value = true
    // 任务结束后清空当前游标，避免用户关闭任务中心后再次手动打开时回到已完成的最后一步。
    activeTaskId.value = ''
    activeStepIndex.value = 0
    save()
    if (shouldSync) {
      queueRemotePatch({ stepChanges: [], cursor: null, setup: null })
    }
  }

  function pause() {
    status.value = 'paused'
    targetMissing.value = false
    taskCenterVisible.value = true
    save()
    syncCursorProgress()
  }

  function retryTarget() {
    targetMissing.value = false
  }

  function skipStep() {
    const taskId = activeTaskId.value
    const stepId = currentStep.value?.id || ''
    if (stepId && !skippedStepIds.value.includes(stepId)) {
      skippedStepIds.value.push(stepId)
    }
    if (stepId) {
      completedStepIds.value = completedStepIds.value.filter(id => id !== stepId)
      deferredStepIds.value = deferredStepIds.value.filter(id => id !== stepId)
    }
    const stepChange = makeStepChange(taskId, stepId, 'skipped')
    if (activeStepIndex.value < currentSteps.value.length - 1) {
      activeStepIndex.value += 1
      targetMissing.value = false
      save()
      queueRemotePatch({
        stepChanges: stepChange ? [stepChange] : [],
        cursor: buildPatchCursor(),
        setup: null,
      })
    }
    else {
      completeTask(false)
      queueRemotePatch({
        stepChanges: stepChange ? [stepChange] : [],
        cursor: null,
        setup: null,
      })
    }
  }

  /**
   * routeList 未返回当前步骤页面时，自动从本次任务步骤中剔除。
   * activeStepIndex 不递增：过滤后下一原始步骤会占据当前索引，
   * 因而界面步骤编号连续，不出现第 5 步后直接跳第 7 步。
   */
  function excludeCurrentStepForUnavailableRoute() {
    const stepId = currentStep.value?.id
    if (!stepId) {
      return
    }
    if (!runtimeUnavailableStepIds.value.includes(stepId)) {
      runtimeUnavailableStepIds.value.push(stepId)
    }
    targetMissing.value = false
    if (!currentSteps.value.length || activeStepIndex.value >= currentSteps.value.length) {
      completeTask(false)
      return
    }
    save()
  }

  function setTargetMissing(missing: boolean) {
    targetMissing.value = missing
  }

  function setStepDataStatus(stepId: string, dataStatus: GuideDataStatus) {
    stepDataStatuses.value = { ...stepDataStatuses.value, [stepId]: dataStatus }
    if (dataStatus === 'ready') {
      deferredStepIds.value = deferredStepIds.value.filter(id => id !== stepId)
    }
  }

  function setLauncherPosition(edge: 'left' | 'right', yRatio: number | null) {
    launcherPosition.value = {
      edge,
      yRatio: yRatio === null ? null : Math.max(0, Math.min(1, yRatio)),
    }
    save()
  }

  function dismissLauncher() {
    launcherDismissed.value = true
    save()
    return true
  }

  function reset() {
    completedTaskIds.value = []
    completedStepIds.value = []
    skippedStepIds.value = []
    deferredStepIds.value = []
    remoteStepChanges.value = []
    stepDataStatuses.value = {}
    activeTaskId.value = ''
    activeStepIndex.value = 0
    status.value = 'idle'
    targetMissing.value = false
    launcherDismissed.value = false
    resetProgressAfterSetupConfirm.value = false
    restartSetupSessionActive = false
    save()
  }

  function restartSetup() {
    // 点击“重新设置”只进入欢迎页，不立即清空旧任务进度。
    // 用户可能只是查看或中途关闭；只有第四步最终确认后才调用 reset 接口。
    restartSetupSessionActive = true
    resetProgressAfterSetupConfirm.value = true
    launcherDismissed.value = false
    closeTaskCenter()
    openWelcome(0)
    save()
  }

  function openManually() {
    // 欢迎设置尚未完成时，悬浮按钮继续打开欢迎设置，并恢复到当前 setupStep。
    if (!initialized.value) {
      openWelcome(setupStep.value)
      return
    }

    // 欢迎设置完成以后，点击悬浮按钮永远先打开“目标学习路径 / 全部任务”页面。
    // 即使 GET 恢复了未完成 cursor，也不能因为点击悬浮按钮直接跳进 GuideTour。
    // 只有用户在任务中心主动点击某个任务，startTask() 才真正进入教程步骤。
    welcomeVisible.value = false
    targetMissing.value = false
    status.value = 'idle'
    openTaskCenter(true)
  }

  async function quickStart() {
    if (setupCompletionInFlight) {
      return
    }

    // 快速开始也走同一个最终确认事务，确保重新设置时旧步骤被一次性覆盖。
    setupExperience.value = 'similar'
    setupGoals.value = ['project']
    setupStep.value = 3
    await completeSetup(false)
  }

  return {
    welcomeVisible,
    taskCenterVisible,
    status,
    experience,
    goals,
    setupStep,
    setupExperience,
    setupGoals,
    showAllTasks,
    activeTaskId,
    activeStepIndex,
    completedTaskIds,
    completedStepIds,
    skippedStepIds,
    deferredStepIds,
    stepDataStatuses,
    runtimeUnavailableStepIds,
    initialized,
    targetMissing,
    launcherPosition,
    launcherDismissed,
    workspaceRoute,
    remoteExists,
    remoteStateLoaded,
    remoteStateLoadFailed,
    remoteSyncing,
    remoteSyncFailed,
    remoteRevision,
    remoteResetEpoch,
    edition,
    tasks,
    allTasksCompleted,
    currentTask,
    currentSteps,
    currentStep,
    recommendedIds,
    setupRecommendedTasks,
    visibleTasks,
    completedVisibleTaskCount,
    progress,
    estimatedMinutes,
    initialize,
    loadRemoteProgress,
    clearSession,
    setAccessibleTaskIds,
    setWorkspaceRoute,
    openWelcome,
    openWelcomeWhenSystemReady,
    deferWelcome,
    nextSetup,
    previousSetup,
    completeSetup,
    openTaskCenter,
    closeTaskCenter,
    toggleAllTasks,
    taskStatus,
    taskEstimatedMinutes,
    startTask,
    next,
    previous,
    completeTask,
    pause,
    retryTarget,
    skipStep,
    excludeCurrentStepForUnavailableRoute,
    setTargetMissing,
    setStepDataStatus,
    setLauncherPosition,
    dismissLauncher,
    reset,
    restartSetup,
    openManually,
    quickStart,
    save,
  }
})

export default useOnboardingStore
